import { DOCUMENT, inject, Injectable } from '@angular/core';
import { ScheduleSidebarService } from '@app/+authenticated/+schedule/employee/schedule-employee-n-period/schedule-sidebar.service';
import { OnboardingDialogResult } from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { OnboardingUtils } from '@app/+onboarding/onboarding.utils';
import { PageFiltersService } from '@app/reducers/page-filters/page-filters.service';
import { DragDropData } from '@app/shared/drag-drop/models/drag-drop-data.model';
import { UserGuideType } from '@reducers/orm/user-guide/user-guide.model';
import { DialogRef, SbDialogService } from '@sb/ui/dialog';
import type { NormalizeOutput } from 'normalizr';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, Subject, take } from 'rxjs';

import { ScheduleNActionService } from '../../../+authenticated/+schedule/shared/schedule-actions/schedule-n-action.service';
import { DragType, SelfOnboardingSegment } from '../../../enums';
import { TrackingService } from '../../../services/tracking.service';
import { SmbHospitalityOnboardingChecklistStep } from '../../../shared/onboarding/onboarding-checklist.model';
import { OnboardingChecklistService } from '../../../shared/onboarding/onboarding-checklist.service';
import { ScheduleShiftDialogComponent } from '../schedule-shift/schedule-shift-dialog.component';

export enum ScheduleDropHandlerResult {
  SKIP,
  SHIFT_NOT_FOUND,
  STEP_COMPLETED,
  STEP_NOT_COMPLETED,
}

@Injectable({
  providedIn: 'root',
})
export class ScheduleOnboardingService {
  private onboardingChecklist = inject(OnboardingChecklistService);
  private dialogService = inject(SbDialogService);
  private pageFiltersService = inject(PageFiltersService);
  private trackingService = inject(TrackingService);
  private sidebarService = inject(ScheduleSidebarService);
  private onboardingUtils = inject(OnboardingUtils);
  private scheduleNActionService = inject(ScheduleNActionService);
  private _document = inject<Document>(DOCUMENT);

  onboardingInitialized = new BehaviorSubject<boolean>(false);

  waitingForShiftScheduled = false;

  selfOnboardingSegment: SelfOnboardingSegment;

  waitingForUserToDragShift$ = new Subject<boolean>();

  private dialogRef: DialogRef<OnboardingDialogResult, ScheduleShiftDialogComponent> | undefined;

  constructor() {
    void combineLatest([this.scheduleNActionService.isDragging, this.waitingForUserToDragShift$])
      .pipe(filter(([isDragging, waitingForUserToDragShift]) => !!isDragging && !!waitingForUserToDragShift))
      .subscribe(() => {
        this.waitingForUserToDragShift$.next(false);
      });
  }

  setFiltersForOnboarding() {
    this.pageFiltersService.setFilters('schedule', {
      showTypes: {
        scheduledShifts: true,
        openShifts: false,
        requiredShifts: false,
        absence: false,
        availability: false,
        event: false,
        teamNotes: false,
        weatherForecast: false,
      },
      showOptions: {
        publishStatus: false,
        totals: false,
        budget: false,
        forecast: false,
        violationsOnly: false,
        includeEmployeesWithoutData: true,
        loanedShifts: false,
        conflicts: false,
        emptyTeams: true,
        compact: false,
      },
      department: {},
      skills: [],
      employeeSearch: '',
    });
  }

  setSelfOnboardingSegment(segment: SelfOnboardingSegment) {
    this.selfOnboardingSegment = segment;
  }

  openDialog() {
    // If dialog is already open, do not open it again
    if (this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialogService.setSize('xxl').open(ScheduleShiftDialogComponent);

    void this.dialogRef.closed.pipe(take(1)).subscribe((result: OnboardingDialogResult) => {
      this.dialogRef = undefined;

      if (result?.action === 'primary') {
        this.sidebarService.openSideBar();
        this.waitingForUserToDragShift$.next(true);
        this.waitingForShiftScheduled = true;

        this.trackingService.event('Onboarding Scheduling Drag Shift Started', {
          onboarding_segment: this.selfOnboardingSegment,
        });
      } else {
        this.onboardingUtils.stripOnboardingParamsFromRoute();
      }
    });
  }

  // If a shift is scheduled, doesn't matter in what way, we want to mark this step complete in case the user was performing the 'add a shift' action.
  shiftScheduled(): Observable<boolean> {
    this.waitingForShiftScheduled = false;
    this.waitingForUserToDragShift$.next(false);

    return this.onboardingChecklist.completeStep(
      UserGuideType.SELF_ONBOARDING_CHECKLIST,
      SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT,
    );
  }

  scheduleDropHandler(response: NormalizeOutput, dragDropData: DragDropData): Observable<ScheduleDropHandlerResult> {
    if (!this.waitingForShiftScheduled || dragDropData.dropZoneData?.dropZoneType !== DragType.SCHEDULE) {
      return of(ScheduleDropHandlerResult.SKIP);
    }

    const resultId = response.result?.[0];
    const scheduledShift = response.entities?.schedules?.[resultId];

    if (!scheduledShift) {
      return of(ScheduleDropHandlerResult.SHIFT_NOT_FOUND);
    }

    this.trackingService.event('Onboarding Scheduling Drag Shift Completed', {
      onboarding_segment: this.selfOnboardingSegment,
      shift_id: parseInt(scheduledShift.id),
    });

    return this.shiftScheduled().pipe(
      map((completed) =>
        completed ? ScheduleDropHandlerResult.STEP_COMPLETED : ScheduleDropHandlerResult.STEP_NOT_COMPLETED,
      ),
    );
  }
}
